import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import axios from "axios";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";
import HaltReportTable from "./boatReportTable";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange"; // ✅ Imported Custom DateRangePicker
import DhundhooLoader from "../Loader/Loader";

function BoatReport() {
  const [boatId, setBoatId] = useState("");
  const [tripData, setTripData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  // ✅ Aaj (Today) ki date nikalne ka logic
  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  // ✅ Set Default States to Today
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  const columns = [
    { Header: "Trip Start Time", accessor: "tripStartTime" },
    { Header: "Trip End Time", accessor: "tripEndTime" },
    { Header: "Start Fuel", accessor: "startFuel" },
    { Header: "End Fuel", accessor: "endFuel" },
    { Header: "Passenger Count", accessor: "passengerCount" },
  ];

  // ✅ Readable date/time format helper
  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString();
  };

  const fetchTripReport = async () => {
    if (!boatId) {
      setErrorMessage("Please select boat");
      openErrorSB();
      return;
    }

    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first");
      openErrorSB();
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setTripData([]);
    setSummary(null);

    try {
      const token = localStorage.getItem("token");

      // ✅ DateRangePicker returns "YYYY-MM-DDTHH:mm". API needs "YYYY-MM-DDTHH:mm:ss"
      // Isliye hum end mein ":00" append kar rahe hain
      const startDate = `${startTime}:00`;
      const endDate = `${endTime}:00`;

      console.log("StartDate:", startDate);
      console.log("EndDate:", endDate);

      const url = `https://v3.dhundhoo.com/boat/trip/admin/tripReport?boatId=${boatId}&startDate=${startDate}&endDate=${endDate}`;

      const response = await axios.post(
        url,
        {},
        {
          headers: {
            "x-dhundhoo-session": token,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data;

      setSummary({
        totalTrips: data.totalTrips || 0,
        totalFuel: data.totalFuelConsumed || 0,
        totalDistance: data.totalDistance ? (data.totalDistance * 0.539957).toFixed(2) : 0,
        totalEngineHours: data.totalEngineHours || 0,
        totalPassengers: data.totalPassengers || 0,
      });

      // ✅ If No Trips
      if (!data.trips || data.trips.length === 0) {
        setErrorMessage("No trips found for selected date range");
        openErrorSB();
        return;
      }

      // ✅ Map Trips To Table - tripStartTime, tripEndTime, startFuel, endFuel, passengerCount
      const rows = data.trips.map((trip) => ({
        tripStartTime: formatDateTime(trip.tripStartTime),
        tripEndTime: formatDateTime(trip.tripEndTime),
        startFuel: trip.startFuel ?? "-",
        endFuel: trip.endFuel ?? "-",
        passengerCount: trip.passengerCount ?? "-",
      }));

      setTripData(rows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to load trip report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setBoatId("");
    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    setTripData([]);
    setSummary(null);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">

                    {/* Boat Single-Select */}
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        options={things || []}
                        getOptionLabel={(option) => option.thingId}
                        value={things.find((t) => t.thingId === boatId) || null}
                        onChange={(e, newValue) =>
                          setBoatId(newValue ? newValue.thingId : "")
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Select Boat" size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#1398DC", // ✅ border on click
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#1398DC", // ✅ label color
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ✅ Custom DateRangePicker */}
                    <Grid item xs={12} md={6}>
                      <div style={{ width: '46%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                        />
                      </div>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={fetchTripReport}
                        disabled={loading}
                        fullWidth
                        size="small"
                      >
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton
                        variant="outlined"
                        color="error"
                        onClick={handleClear}
                        disabled={loading}
                        fullWidth
                        size="small"
                      >
                        CLEAR
                      </MDButton>
                    </Grid>
                  </Grid>
                </LocalizationProvider>

                {/* Summary Section */}
                {summary && (
                  <MDBox mt={4}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography>Total Trips: {summary.totalTrips}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>Fuel: {summary.totalFuel}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        {/* ✅ Displaying converted distance with unit */}
                        <Typography>Distance: {summary.totalDistance} NM</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>
                          Engine Hours: {summary.totalEngineHours}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>
                          Passengers: {summary.totalPassengers}
                        </Typography>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}

                {/* Error */}
                {errorMessage && (
                  <MDBox mt={2}>
                    <Alert severity="error">{errorMessage}</Alert>
                  </MDBox>
                )}

                {/* Table */}
                <MDBox mt={4} display="flex" justifyContent="center">
                  {loading ? (
                    <DhundhooLoader />
                  ) : (
                    <HaltReportTable
                      table={{ columns, rows: tripData }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries
                      noEndBorder
                    />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="Trip Report"
        content="Trip report loaded successfully"
        open={successSB}
        onClose={closeSuccessSB}
      />

      <MDSnackbar
        color="error"
        icon="warning"
        title="Trip Report"
        content="No trips found or API error"
        open={errorSB}
        onClose={closeErrorSB}
      />
    </DashboardLayout>
  );
}

export default BoatReport;